import { CollectionRecord } from "@common/data-models/records/collection-record";
import { TimingFormat } from "@common/utils/timing-format";
import { Record } from "@common/data-models/records/record";
import { StoryRecord } from "@live/data-models/records/story-record";
import { LiveService } from "@live/services/live-service";
import moment from 'moment-timezone';


export class GridCell {
    private _isHeader: boolean = false;
    public get isHeader(): boolean { return this._isHeader; }
    public set isHeader(value: boolean) { if (this._isHeader != value) this._isHeader = value; }

    private _rowSpan: number = 1;
    public get rowSpan(): number { return this._rowSpan; }
    public set rowSpan(value: number) { if (this._rowSpan != value) this._rowSpan = value; }

    private _colSpan: number = 1;
    public get colSpan(): number { return this._colSpan; }
    public set colSpan(value: number) { if (this._colSpan != value) this._colSpan = value; }

    private _cssClass: string = "";
    public get cssClass(): string { return this._cssClass; }
    public set cssClass(value: string) { if (this._cssClass != value) this._cssClass = value; }

    private _content: string = "";
    public get content(): string { return this._content; }
    public set content(value: string) { if (this._content != value) this._content = value; }

    private _validation: string = "";
    public get validation(): string { return this._validation; }
    public set validation(value: string) { if (this._validation != value) this._validation = value; }

    private _width: string = "auto";
    public get width(): string { return this._width; }
    public set width(value: string) { if (this._width != value) this._width = value; }

    private _align: string = "";
    public get align(): string { return this._align; }
    public set align(value: string) { if (this._align != value) this._align = value; }
}

export class GridData {
    public cells: Array<Array<GridCell>> = [];
    public static liveService: LiveService;



    public setSize(rows: number, cols: number) {
        // Add rows if needed
        while (this.cells.length < rows) {
            this.cells.push([]);
        }
        // Remove extra rows
        this.cells.splice(rows);
        for (let row of this.cells) {
            // Add cols if needed
            while (row.length < cols) {
                row.push(new GridCell());
            }
            // Remove extra cols
            row.splice(cols);
        }
    }

    public calculateRowSpan(children: Array<Record>, index: number): number {
        if (!(<StoryRecord>children[index]).Segment) {   // old field name - segment, new field name - Segment
            return 1;
        } else {
            let currentSlug = (<StoryRecord>children[index]).storySlug;
            if ((index > 0) && ((<StoryRecord>children[index - 1]).storySlug == currentSlug)) {
                // 0 if previous has the same slug
                return 0;
            } else {
                // iterate until we find a different slug or story with no segments
                let ret = 1;
                for (let i = index + 1; i < children.length; i++) {
                    // old field name - segment, new field name - Segment
                    if ((!(<StoryRecord>children[i]).Segment) || ((<StoryRecord>children[i]).storySlug != currentSlug)) return ret;
                    ret++;
                }
                return ret;
            }
        }
    }

    public populate(collection: CollectionRecord) {
        let children = collection.getChildren();
        this.setSize(children.length + 1, collection.layout.columns.length);

        // Column headers
        for (let col = 0; col < collection.layout.columns.length; col++) {
            this.cells[0][col].isHeader = true;
            this.cells[0][col].content = collection.layout.columns[col].displayName;
        }

        // Data
        for (let row = 0; row < children.length; row++) {
            let rowSpan = this.calculateRowSpan(children, row);
            let status = children[row].getAs<string>("Status");
            let breakBool = children[row].getAs<boolean>("Break");

            for (let col = 0; col < collection.layout.columns.length; col++) {
                let cssClass = "";
                if (children[row] && children[row].guid === "0") {
                    cssClass += "colBlackLine ";
                } else if ((col == 1) || (col == 3)) {
                    switch (status) {
                        case "2":
                            cssClass += "colNoText ";
                            break;
                        case "3":
                            cssClass += "colApprovalPending ";
                            break;
                        case "4":
                            cssClass += "colApproved ";
                            break;
                    }
                    cssClass += "colWidth ";
                } else {
                    if (breakBool) cssClass += "RowBreak ";
                }

                let colName = collection.layout.columns[col].name;
                let colValue: any = null;
                switch (colName) {
                    case "Slug":
                        colValue = (<StoryRecord>children[row]).storySlug;
                        break;
                    case "Segment":
                        colValue = (<StoryRecord>children[row]).Segment;  // old field name - segment, new field name - Segment
                        break;
                    default:
                        if (children[row].containsKey(colName + "Formatted")) {
                            colValue = children[row].get(colName + "Formatted");
                        } else {
                            colValue = children[row].get(colName);
                        }
                }
                this.cells[row + 1][col].isHeader = false;
                if (collection.layout.columns[col].validation == "CheckBox") {
                    this.cells[row + 1][col].content = (colValue ? colValue : false);
                    cssClass += "text-center ";
                } else {
                    this.cells[row + 1][col].content = this.gridColumnValueConverter({
                        recordValue: colValue,
                        validationType: collection.layout.columns[col].validation,
                        columnName: colName,
                        record: children[row]
                    });
                }
                this.cells[row + 1][col].validation = collection.layout.columns[col].validation.toUpperCase();
                this.cells[row + 1][col].cssClass = cssClass;
                if (collection.layout.columns[col].width) {
                    this.cells[row + 1][col].width = collection.layout.columns[col].width + "px";
                } else {
                    this.cells[row + 1][col].width = "auto";
                }
                this.cells[row + 1][col].align = collection.layout.columns[col].justification.toLowerCase();
                if ((colName == "RowStatus") || (colName == "Slug")) {
                    this.cells[row + 1][col].rowSpan = rowSpan;
                }
            }
        }
    }

    public gridColumnValueConverter(parameters: {
        recordValue: any,
        validationType: string,
        columnName: string,
        record: Record
    }) {
        let columnNameLower = parameters.columnName.toLowerCase();
        let convertedFormat = "";
        // depending on the columnValidationType we need to apply different conversions
        switch (parameters.validationType) {
            case 'Duration':
                //console.log("calculating duration column for row: " + parameters.recordPointer.guid());
                // First see if the webservice provided us with a formatted value
                if (parameters.record != null && parameters.record.containsKey(parameters.columnName + "Formatted")) {
                    convertedFormat = parameters.record.get(parameters.columnName + "Formatted");
                } else {
                    if ((parameters.recordValue || parameters.recordValue == 0) && !(columnNameLower == 'actual' && (parameters.recordValue == 0 || parameters.recordValue == "00:00:00" || parameters.recordValue == "0:00"))) {
                        convertedFormat = TimingFormat.formatTime(parameters.recordValue).split('.')[0]; // .split('.')[0] fixes formatting done by TimingFormat.formatTime
                    } else if (parameters.recordValue || parameters.recordValue == 0) {
                        convertedFormat = TimingFormat.formatTime(parameters.recordValue).split('.')[0]; // .split('.')[0] fixes formatting done by TimingFormat.formatTime
                    } else if ((!parameters.recordValue && parameters.recordValue != 0) && (columnNameLower == 'actual' && parameters.record.containsKey("ReadTime"))) {
                        convertedFormat = TimingFormat.formatTime(parameters.record.get("ReadTime")).split('.')[0]; // .split('.')[0] fixes formatting done by TimingFormat.formatTime
                    } else {
                        convertedFormat = "0:00";
                    }
                }
                break;
            case 'Time':
                if ((parameters.recordValue) && (parameters.recordValue != "1800-01-01T00:00:00") && (parameters.recordValue != "1800-01-01T00:00:00Z")) {
                    convertedFormat = TimingFormat.formatTimeHMMSSADST(parameters.recordValue);
                    if (convertedFormat == 'Invalid date') {
                        convertedFormat = parameters.recordValue;
                    }
                } else {
                    convertedFormat = "";
                }
                break;

            case 'MOSDateTime':
                if ((parameters.recordValue) && (parameters.recordValue != "1800-01-01T00:00:00") && (parameters.recordValue != "1800-01-01T00:00:00Z")) {
                    convertedFormat = TimingFormat.convertMOSDateTime(parameters.recordValue);
                } else {
                    convertedFormat = "";
                }
                break;
            case 'CheckBox':
                break;
            case 'Text':
                let MOSColumns = columnNameLower == 'mosabstracts' || columnNameLower == 'mosids' || columnNameLower == 'mositemeddurations' || columnNameLower == 'mosobjslugs' || columnNameLower == 'mosstatus' || columnNameLower == 'mosslugs' || columnNameLower == 'mosslugs1l' || columnNameLower == 'mosstorystatus' || columnNameLower == 'mostimes' || columnNameLower == 'moschannels' || columnNameLower == 'mositemdurations';
                if (parameters.recordValue && MOSColumns) {
                    if (columnNameLower == 'mositemdurations') {
                        convertedFormat = "";
                        let values = parameters.recordValue.split("\r\n");
                        for (let i = 0; i < values.length; i++) {
                            let valueFormat = "";
                            if (values[i].indexOf(":") == -1) {
                                valueFormat = TimingFormat.formatTime(Math.round(values[i]));
                            } else {
                                valueFormat = values[i];
                            }
                            if (valueFormat == 'Invalid date') {
                                valueFormat = values[i];
                            }

                            convertedFormat += valueFormat + "<br>";
                        }
                    } else if (columnNameLower == 'mostimes') {
                        convertedFormat = "";
                        let values = parameters.recordValue.split("\r\n");
                        for (let i = 0; i < values.length; i++) {
                            let valueFormat = "";
                            if (values[i].indexOf(":") == -1) {
                                //parse YYYYMMDDHHMMSS..millis from service in a format which moment.js can understand; 20140306114847000 is from service 
                                let convertedVal = values[i].replace(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{3})$/, "$1-$2-$3T$4:$5:$6");  // "2014/03/06 11:48:47"
                                valueFormat = TimingFormat.formatTimeHMMSSA(convertedVal);
                            } else {
                                valueFormat = values[i];
                            }
                            if (valueFormat == 'Invalid date') {
                                valueFormat = values[i];
                            }

                            convertedFormat += valueFormat + "<br>";
                        }
                    } else if (columnNameLower == 'mosids') {
                        convertedFormat = "";
                        let values = parameters.recordValue.split("\r\n");

                        for (let i = 0; i < values.length; i++) {
                            let value = values[i].trim();
                            let valueFormat = "";

                            let mosDevice = GridData.liveService.getMosDeviceByID(value);
                            //console.log(mosDevice);
                            //let mosDevice = null;
                            if (mosDevice && mosDevice.AliasValue) {
                                valueFormat = mosDevice.AliasValue;
                            } else {
                                valueFormat = value;
                            }
                            convertedFormat += valueFormat + "<br>";
                        }
                    } else {
                        convertedFormat = parameters.recordValue.replace(/\r\n/g, "<br>");
                    }
                } else if ((columnNameLower == "modby") || (columnNameLower == "creator") || (columnNameLower == "owner")) {
                    if (parameters.recordValue) {
                        convertedFormat = this.lookupUserFullName(parameters.recordValue);
                    } else {
                        convertedFormat = "";
                    }
                } else {
                    if (parameters.recordValue && parameters.recordValue) {
                        convertedFormat = parameters.recordValue.replace(/\r\n/g, "<br>");
                    }
                }
                break;
            case 'MOSAutomation':
                if (parameters.recordValue) {
                    if (Object.prototype.toString.call(parameters.recordValue) === '[object Array]') {
                        parameters.recordValue.forEach(function (item) {
                            convertedFormat += item + '<br>';
                        });
                    } else {
                        convertedFormat = parameters.recordValue.replace(/\r\n/g, "<br>");
                    }
                }
                break;
            case 'MOSAutomationDuration':
                convertedFormat = "";
                if (parameters.recordValue) {
                    let values = parameters.recordValue.split("\r\n");
                    for (let i = 0; i < values.length; i++) {
                        let valueFormat = "";
                        if (values[i].trim() != "") {
                            let _objTB: any = 1;
                            let _regex = new RegExp("<objTB>(.*?)<\/objTB>", "g");

                            // search the text commands
                            /*if (parameters.recordPointer.textCommandValues()[i].match(_regex)) {
                                _objTB = _regex.exec(parameters.recordPointer.textCommandValues()[i])[1];
                            }*/

                            valueFormat = TimingFormat.formatTime(Math.round(values[i].replace("*", "") / _objTB));
                        } else {
                            valueFormat = values[i];
                        }
                        if (valueFormat == 'Invalid date') {
                            valueFormat = values[i];
                        }

                        convertedFormat += valueFormat + "<br>";
                    }
                }
                break;
            case "StaffIDList":
                let ret = "";
                if (parameters.recordValue) {
                    ret = this.staffIDLookup(parameters.recordValue);
                }
                break;
            case 'Date':
                if ((parameters.recordValue) && (moment(parameters.recordValue, "YYYY-MM-DDTHH:mm:ss Z").format('L') != "01/01/1800") && (moment(parameters.recordValue, "YYYY-MM-DDTHH:mm:ss Z").format('L') != "12/31/1799")) {
                    convertedFormat = TimingFormat.formatDateDST(parameters.recordValue);
                } else {
                    convertedFormat = "";
                }
                break;
            default:
                if (columnNameLower == "modby") {
                    if (parameters.recordValue) {
                        convertedFormat = this.lookupUserFullName(parameters.recordValue);
                    } else {
                        convertedFormat = "";
                    }
                } else {
                    if (parameters.recordValue) {
                        convertedFormat = parameters.recordValue;
                    }
                }
                break;
        }

        return convertedFormat;
    }

    private staffIDLookup(data) {
        let ret = "";
        for (let i = 0; i < data.length; i++) {
            let s = data[i];
            if (s != "") {
                let t = s.split(".");
                ret += this.lookupUserFullName(t[t.length - 1]);
            }
            ret += "<br>";
        }
        return ret;
    }

    private lookupUserFullName(userId) {
        let user = GridData.liveService.globalTablesService.Staff[userId];
        if (user) {
            return user.DisplayName;
        }
        return "";
    }
}
